import ServiceLayout from "@/components/ServiceLayout";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

export default function Muenchen() {
  return (
    <ServiceLayout
      title="Umzugsunternehmen in München"
      description="Ihr Experte für Umzüge in und um München. Wir navigieren sicher durch den Großstadtdschungel und bringen Ihre Möbel sicher ans Ziel."
      image="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/6936df93efa09c211e149b1d.jpg"
      heroBg="https://vibe.filesafe.space/1781514922662573702/assets/78bddd48-cffe-4910-90f5-e84b9cda715b.png"
      content={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Ihr Umzug in München
          </h2>
          <p className="text-lg text-neutral-600">
            Ein Umzug in der bayerischen Landeshauptstadt bringt oft logistische Herausforderungen mit sich. Enger Verkehr, schwierige Parkplatzsituationen und schmale Treppenhäuser sind für uns jedoch kein Problem.
          </p>
          <p className="text-lg text-neutral-600">
            Wir organisieren rechtzeitig Halteverbotszonen, bringen ausreichend Verpackungsmaterial mit und sorgen mit unserem erfahrenen Team für einen zügigen und sicheren Umzug in München.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Einrichtung von Halteverbotszonen in München",
              "Erfahren im Großstadt-Verkehr",
              "Sicherer Transport auch in engen Treppenhäusern",
              "Kompletter Service aus einer Hand"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-neutral-800 font-medium">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </>
      }
      extendedContent={
        <div className="bg-white">
          <section className="py-16 bg-neutral-50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Ihr professionelles Umzugsunternehmen in München – Entspannt durch die Großstadt</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Ein Umzug in der bayerischen Landeshauptstadt München ist oft mit besonderen logistischen Herausforderungen verbunden. Dichter Verkehr, knappe Parkplätze und schmale Treppenhäuser in Altbauten fordern starke Nerven und eine perfekte Organisation. Genau dafür sind wir da: Als Ihr erfahrenes Umzugsunternehmen für München und das Umland nehmen wir Ihnen den Stress ab und sorgen für einen reibungslosen Ablauf.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Egal ob Sie nach Schwabing, Bogenhausen, Haidhausen oder ins Umland ziehen – wir kennen die Stadtviertel und ihre Besonderheiten. Wir kümmern uns rechtzeitig um die Beantragung und Aufstellung von Halteverbotszonen, damit am Umzugstag alles reibungslos läuft. Mit modernen Fahrzeugen, erfahrenen Möbelpackern und dem richtigen Equipment meistern wir jeden Großstadt-Umzug sicher und termingerecht.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Kostenloses Angebot anfordern
                      </Button>
                    </QuoteModal>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="tel:+4915888640762">
                        <Phone className="w-5 h-5 mr-2" />
                        +49 15888 640762
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Privatumzüge in München</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Von der kleinen Studenten-WG im Univiertel bis zur großen Familienvilla im Vorort – wir passen unsere Umzugsleistungen in München genau an Ihre Bedürfnisse an. Ob nur Transport oder Full-Service-Umzug mit Einpackservice, wir sind für Sie da.
                    </p>
                  </div>
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Büroumzüge & Firmenumzüge</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Wenn Ihr Unternehmen in München den Standort wechselt, ist Effizienz gefragt. Wir transportieren Ihre IT-Geräte, Akten und Büromöbel sicher und schnell, damit Sie den Geschäftsbetrieb so rasch wie möglich wieder aufnehmen können.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Unser Full-Service für Ihren Münchner Umzug</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Umzüge in München erfordern oft mehr als nur den Transport von Kisten. Deshalb bieten wir Ihnen ein umfassendes Dienstleistungsportfolio. Unsere erfahrenen Monteure übernehmen die Demontage und fachgerechte Montage Ihrer Möbel, inklusive komplexer Küchen und Schranksysteme. So sparen Sie wertvolle Zeit und Nerven.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Darüber hinaus kümmern wir uns um sämtliche Elektro- und Lichtinstallationen. Wir schließen Deckenleuchten an, installieren Herde und Waschmaschinen und sorgen dafür, dass Ihr neues Zuhause schnell bezugsfertig ist. Sollten vor dem Einzug oder nach dem Auszug noch Renovierungsarbeiten wie Bodenverlegen oder Pflasterarbeiten anstehen, erledigen wir auch das professionell und zuverlässig.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Jetzt beraten lassen
                      </Button>
                    </QuoteModal>
                  </div>
                </div>

                <div className="bg-primary/5 p-8 border-l-4 border-primary mt-8">
                  <h3 className="font-heading text-2xl font-bold mb-3">Transparente Preise ohne versteckte Kosten</h3>
                  <p className="text-neutral-700 leading-relaxed">
                    Ein Umzug in München muss nicht zur Kostenfalle werden. Wir bieten Ihnen nach einer kostenlosen und unverbindlichen Vor-Ort-Besichtigung ein faires Festpreisangebot. Sie wissen von Anfang an, welche Kosten auf Sie zukommen. Zudem sind Ihre Möbel und Kartons während des gesamten Umzugs durch unsere umfassende Transportversicherung abgesichert.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-neutral-950 text-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-3xl font-bold mb-6">Ihre Vorteile mit Easy Umzug in München</h2>
                <div className="grid sm:grid-cols-3 gap-8 mt-12 text-left">
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Großstadt-Erfahrung</h3>
                    <p className="text-neutral-400">Wir kennen die Herausforderungen des Münchner Verkehrs und meistern auch Umzüge in engen Altbau-Treppenhäusern.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Halteverbots-Service</h3>
                    <p className="text-neutral-400">Wir übernehmen die behördliche Beantragung und das Aufstellen der Schilder für einen reibungslosen Ablauf.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Alles aus einer Hand</h3>
                    <p className="text-neutral-400">Von der Möbelmontage bis zu weiteren Handwerksleistungen bieten wir Ihnen alles rund um den Umzug.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      }
    />
  );
}
